import {ControlledAccordion, useAccordionProvider} from "@szhsin/react-accordion";
import {useState} from "react";
import {AccordionItem} from "@/components/sections/B7Accordion/AccordionItem";
import {Text} from "@/elements/text";
import WebpPicture from "@/elements/WebpPicture";
import type {SectionSections} from "@/generated/api";
import SectionWrapper from "@/layouts/SectionWrapper";
import {getResponsiveImage} from "@/utils/cardImage";
import {imageOf} from "@/utils/index";
import type {Translation} from "@/utils/translation";
import {
  Button,
  ButtonContainer,
  Container,
  ImageContainer,
  ImageWrapper,
  Overlay,
  RubriqueImage,
  Wrapper,
} from "./style";

interface B14RubriquesProps {
  sectionData: SectionSections;
  isFirstSection?: boolean;
  isLastSection?: boolean;
  id?: string;
  translation?: Translation;
}

const B14Rubriques = ({
  sectionData,
  isFirstSection,
  isLastSection,
  id,
  translation,
}: B14RubriquesProps) => {
  const {sections, bg_color} = sectionData;
  const showAllTranslation = translation?.showAll;
  const hideAll = translation?.hide;
  const [showAll, setShowAll] = useState(false);
  const defaultImages = sections ? sections.slice(0, 8) : [];
  const showMore = sections ? sections.length > 8 : false;
  const anotherImages = showMore ? sections.slice(8, sections.length) : [];

  const providerValue = useAccordionProvider({
    allowMultiple: true,
    transition: true,
    transitionTimeout: 250,
  });
  const {toggleAll} = providerValue;

  // The image of a tile: the copy of the srcset that fits its box, in WebP when the API has made it
  const tileImage = (rawImage: (typeof defaultImages)[number]["image"]) => {
    const image = imageOf(rawImage);
    const responsive = getResponsiveImage({image, layout: "rubriques"});
    return (
      <WebpPicture webp={image?.webp} sizes={responsive.sizes}>
        <RubriqueImage
          src={image?.url}
          alt={image?.alt || "rubriques image"}
          title={image?.title || "rubriques image"}
          {...responsive}
        />
      </WebpPicture>
    );
  };

  return (
    <SectionWrapper
      isFirstSection={isFirstSection}
      isLastSection={isLastSection}
      id={id}
      backgroundColor={bg_color}
      styles={{}}
    >
      <Container>
        {defaultImages.map(({image: rawImage, title, link}, i) => (
          <ImageWrapper key={i}>
            <ImageContainer showAll={showAll}>
              {tileImage(rawImage)}
              <Overlay target={link?.target} to={link?.url}>
                <Text
                  type="tag"
                  styles={{
                    position: "absolute",
                    width: "100%",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    color: "#fff ",
                    textAlign: "center",
                  }}
                >
                  {title}
                </Text>
              </Overlay>
            </ImageContainer>
          </ImageWrapper>
        ))}
      </Container>
      <Wrapper showAll={showAll} show={showMore}>
        <ControlledAccordion providerValue={providerValue}>
          <AccordionItem
            styles={{border: "none"}}
            showIcon={false}
            buttonProps={{"aria-hidden": true, tabIndex: -1}}
          >
            <Container>
              {anotherImages.map(({image: rawImage, title, link}, i) => (
                <ImageWrapper key={i}>
                  <ImageContainer showAll={showAll}>
                    {tileImage(rawImage)}
                    <Overlay target={link?.target} to={link?.url}>
                      <Text
                        type="tag"
                        styles={{
                          position: "absolute",
                          width: "100%",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          color: "#fff ",
                          textAlign: "center",
                        }}
                      >
                        {title}
                      </Text>
                    </Overlay>
                  </ImageContainer>
                </ImageWrapper>
              ))}
            </Container>
          </AccordionItem>
        </ControlledAccordion>
        <ButtonContainer
          showAll={showAll}
          onClick={() => {
            toggleAll();
            setShowAll((prev) => !prev);
          }}
        >
          {showMore && (
            <Button>
              <Text type={"bodyRegular"} style={{color: "#FFFFFF"}}>
                {showAll ? `${hideAll}` || "Hide" : `${showAllTranslation}` || "Show all"}
              </Text>
            </Button>
          )}
        </ButtonContainer>
      </Wrapper>
    </SectionWrapper>
  );
};

export default B14Rubriques;
